import type { AppearanceState } from "../appearance/types";

/**
 * Bump this whenever the persisted shape changes, and add a migration in
 * storage.ts covering the previous version. A returning user must never see a
 * crash or a blank screen because the shape moved under them.
 */
export const SCHEMA_VERSION = 1;

/**
 * Deliberately unversioned key: the version lives INSIDE the payload. Putting
 * it in the key would orphan old data and make migration impossible.
 */
export const STORAGE_KEY = "3aam.session";

/** Writes are debounced by this much, then flushed hard on tab teardown. */
export const WRITE_DEBOUNCE_MS = 400;

export type Locale = "en" | "ar";

/**
 * Small, synchronous UI state ONLY — this is the localStorage half of the split.
 * Rate snapshots, currency metadata and chart series live in IndexedDB, because
 * they are large and are not needed before first paint.
 */
export interface SessionState {
  readonly v: number;
  readonly base: string;
  /**
   * The RAW string the user typed, not a parsed number. "12." and "12" are
   * different things and must both come back exactly as they were typed —
   * round-tripping through Number() would silently rewrite the user's input
   * mid-edit.
   */
  readonly amount: string;
  readonly quotes: readonly string[];
  readonly locale: Locale;
  readonly appearance: AppearanceState;
}

/** Guards against a corrupt list eating memory or the UI. */
export const MAX_QUOTES = 50;

/** Matches a partially-typed amount, including trailing-dot states like "12.". */
export const AMOUNT_PATTERN = /^\d{0,15}(\.\d{0,8})?$/;
